import type {
  AppointmentForm,
  SchedulerDateTime,
} from '@devexpress/dx-react-scheduler';
import dayjs from 'dayjs';
import React, { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { RRule } from 'rrule';

import { EffectiveDateTimeInput } from '@components/DateInput';
import {
  HorizontalFlex,
  VerticalFlex,
} from '@components/RecordCard/RecordCard.style';
import TimeSlotInput from '@components/TimeSlotInput/TimeSlotInput';

import { REPEAT_TYPES } from '@constants/date';

import type { RoutineEvent, TimeSlot } from '@models/routine';
import { RoutineRecord } from '@models/routine';

import { changeRecurrenceFrequency, getRRuleFrequency } from '@utils/routines';

import { RoutineBaseFormWrapper } from './RoutineBaseForm.style';

const messages = defineMessages({
  routineName: {
    id: 'routineBaseForm.name',
    defaultMessage: 'Routine name',
  },
  startTime: {
    id: 'routineBaseForm.startTime',
    defaultMessage: 'Start time',
  },
  endTime: {
    id: 'routineBaseForm.endTime',
    defaultMessage: 'End time',
  },
  times: {
    id: 'routineBaseForm.times',
    defaultMessage: 'Times',
  },
  instructions: {
    id: 'routineBaseForm.instructions',
    defaultMessage: 'Instructions',
  },
  repeat: {
    id: 'routineBaseForm.repeat',
    defaultMessage: 'Repeat',
  },
  date: {
    id: 'GLOBAL.date',
    defaultMessage: 'Date',
  },
});

/**
 * Form containing base routine properties not related to recurrence
 */
const RoutineBaseForm = ({
  routineData,
  ...props
}: AppointmentForm.BasicLayoutProps & {
  routineData: RoutineEvent;
}) => {
  const {
    onFieldChange,
    textEditorComponent: TextEditor,
    labelComponent: Label,
  } = props;
  const { formatMessage } = useIntl();

  const {
    title,
    instructions,
    startDate,
    rRule,
    endDate,
    recurrenceStartDate,
  } = routineData;
  const timeSlots = RoutineRecord.getTimeSlots(routineData);

  const duration = RoutineRecord.getRecurrenceDuration(startDate, endDate);

  const displayedSlots: TimeSlot[] = timeSlots.length
    ? timeSlots
    : [
        {
          startTime: new Date(startDate),
          endTime: endDate
            ? new Date(endDate)
            : dayjs(startDate).add(duration, 'minutes').toDate(),
        },
      ];

  const rRuleBackup = rRule?.valueOf();

  const changeTitle = useCallback(
    (title: string) => onFieldChange({ title }),
    [onFieldChange]
  );
  const changeInstructions = useCallback(
    (instructions: string) => onFieldChange({ instructions }),
    [onFieldChange]
  );
  const changeStartDate = useCallback(
    (startDate: SchedulerDateTime) =>
      onFieldChange({ recurrenceStartDate: startDate }),
    [onFieldChange]
  );
  const changeTimeSlots = useCallback(
    (timeSlots: TimeSlot[] | undefined, duration: number) => {
      let byweekday: number[] = [];
      let options: any = {};
      if (rRule) {
        ({ options } = RRule.fromString(rRule));
        ({ byweekday } = options);
      }
      const byhour: number[] = [];
      const byminute: number[] = [];

      timeSlots?.forEach(({ startTime }) => {
        byhour.push(startTime.getHours());
        byminute.push(startTime.getMinutes());
      });

      const bysetpos: number[] = [];
      const N = timeSlots?.length ?? 0;

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      for (const w of [...Array(byweekday?.length || 1).keys()]) {
        const loops = [...Array(N).keys()];
        for (const j of loops) {
          const remaining = loops.length - (j + 1);
          bysetpos.push(N * (j + 1) - remaining + w * N ** 2);
        }
      }

      const newRule = new RRule({
        ...options,
        byhour,
        byminute,
        bysetpos,
      }).toString();

      onFieldChange({
        rRule: newRule,
        startDate: new Date(startDate),
        endDate: dayjs(startDate).add(duration, 'minutes').toDate(),
      });
    },
    [onFieldChange, rRule, startDate]
  );
  const handleChangeFrequency = (
    repeatType: string,
    rRule: string | undefined,
    startDate: Date,
    action: (change: any) => void
  ) => {
    const rruleRepeatType = getRRuleFrequency(repeatType);
    let nextRRule = '';
    if (repeatType !== 'never') {
      nextRRule = changeRecurrenceFrequency(rRule, rruleRepeatType, startDate);
    }
    action({ rRule: nextRRule });
  };
  const changeFrequency = useCallback(
    (value: boolean) =>
      handleChangeFrequency(
        value ? REPEAT_TYPES.DAILY : REPEAT_TYPES.NEVER,
        value ? rRuleBackup : rRule,
        new Date(startDate),
        onFieldChange
      ),
    [rRuleBackup, rRule, startDate, onFieldChange]
  );

  useEffect(() => {
    if (!rRule) {
      changeFrequency(true);
    }
    if (!rRule?.startsWith('DTSTART')) {
      changeTimeSlots(
        [
          {
            startTime: new Date(startDate),
            endTime: endDate
              ? new Date(endDate)
              : dayjs(startDate).add(15, 'minutes').toDate(),
          },
        ],
        15
      );
    }
  }, [rRule, changeFrequency, changeTimeSlots, startDate, endDate]);

  return (
    <RoutineBaseFormWrapper border={Boolean(rRule)}>
      <Label text={formatMessage(messages.routineName)} />
      <TextEditor
        onValueChange={(value) => changeTitle(value)}
        placeholder={formatMessage(messages.routineName)}
        readOnly={false}
        type="titleTextEditor"
        value={title ?? ''}
      />
      <div style={{ height: 14 }} />
      <Label text={formatMessage(messages.date)} />
      <HorizontalFlex justifyContent="flex-start" marginLeft={8}>
        <VerticalFlex>
          <EffectiveDateTimeInput
            type="date"
            value={
              recurrenceStartDate
                ? new Date(recurrenceStartDate).toISOString().split('T')[0]
                : undefined
            }
            onChange={(val) => changeStartDate(val || '')}
          />
        </VerticalFlex>
      </HorizontalFlex>
      <div style={{ height: 14 }} />
      <Label text={formatMessage(messages.times)} />

      <TimeSlotInput
        duration={duration}
        onChange={changeTimeSlots}
        timeSlots={displayedSlots}
      />
      <div style={{ height: 14 }} />
      <Label text={formatMessage(messages.instructions)} />
      <TextEditor
        onValueChange={(value) => changeInstructions(value)}
        placeholder={formatMessage(messages.instructions)}
        readOnly={false}
        type="multilineTextEditor"
        value={instructions ?? ''}
      />
    </RoutineBaseFormWrapper>
  );
};

export default RoutineBaseForm;
